import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../userEndApi/apiClient';

const CurrencyContext = createContext();

export const useCurrency = () => {
    const context = useContext(CurrencyContext);
    if (!context) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return context;
};

export const CurrencyProvider = ({ children }) => {
    const [currencies, setCurrencies] = useState([]);
    const [selectedCurrency, setSelectedCurrency] = useState(() => {
        const saved = localStorage.getItem('selected_currency');
        return saved ? JSON.parse(saved) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCurrencies = async () => {
            try {
                const response = await apiClient.get('/homepage/get-currencies');
                if (response.data.success) {
                    const data = response.data.data;
                    setCurrencies(data.currencies || []);

                    // If no currency selected in localStorage, use default from API
                    if (!selectedCurrency && data.default) {
                        handleCurrencyChange(data.default);
                    }
                }
            } catch (error) {
                console.error("Error fetching currencies:", error);
                // Fallback to basic USD if API fails
                if (!selectedCurrency) {
                    const fallback = { name: 'USD', icon: '$', position: 'left', rate: '1.00', code: 'USD' };
                    setCurrencies([fallback]);
                    handleCurrencyChange(fallback);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchCurrencies();
    }, []);

    const handleCurrencyChange = (currency) => {
        setSelectedCurrency(currency);
        localStorage.setItem('selected_currency', JSON.stringify(currency));
    };

    /**
     * Converts an amount from base currency to the selected currency.
     * Assumes base amount is in the currency where rate is 1.0 (usually USD).
     */
    const convertPrice = (amount) => {
        if (!selectedCurrency || !selectedCurrency.rate) return amount;
        return Number.parseFloat(amount) * Number.parseFloat(selectedCurrency.rate);
    };

    /**
     * Formats an amount according to selected currency symbol and position.
     */
    const formatPrice = (amount) => {
        if (!selectedCurrency) return `$${amount}`;

        const converted = convertPrice(amount);
        const { icon, position } = selectedCurrency;

        // Format with 2 decimal places
        const formattedAmount = converted.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        return position === 'left' ? `${icon}${formattedAmount}` : `${formattedAmount}${icon}`;
    };

    return (
        <CurrencyContext.Provider value={{
            currencies,
            selectedCurrency,
            setSelectedCurrency: handleCurrencyChange,
            formatPrice,
            convertPrice,
            loading
        }}>
            {children}
        </CurrencyContext.Provider>
    );
};

